/**
 * Portal API client.
 *
 * The portal is served from app.aerogreen.garden and the API answers on the
 * same host, so requests are same-origin in production. In development the
 * API is elsewhere, hence the override.
 */
const BASE =
  (typeof window !== 'undefined' && window.RUNTIME_CONFIG?.API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  '';

const TOKEN_KEY = 'aerogreen.token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

async function request(path, { method = 'GET', body, auth = true } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE}/api/portal${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (response.status === 401) {
    // The session is gone. Clear it rather than leaving a dead token that
    // makes every subsequent screen fail in a different way.
    clearToken();
    throw new Error('Your session expired. Please sign in again.');
  }

  const text = await response.text();
  const data = text ? JSON.parse(text) : {};

  if (!response.ok) {
    // The API's `detail` is written to be read by a person — surface it
    // rather than a status code.
    throw new Error(data.detail || `Request failed (${response.status})`);
  }
  return data;
}

export const api = {
  login: (email, password) =>
    request('/auth/login', { method: 'POST', body: { email, password }, auth: false }),
  signup: (email, password, name) =>
    request('/auth/signup', { method: 'POST', body: { email, password, name }, auth: false }),
  me: () => request('/me'),
  logout: () => request('/auth/logout', { method: 'POST' }),

  systems: () => request('/systems'),
  createSystem: (name, kind) => request('/systems', { method: 'POST', body: { name, kind } }),
  updateSystem: (systemId, updates) =>
    request(`/systems/${systemId}`, { method: 'PATCH', body: updates }),

  createZone: (systemId, zone) =>
    request(`/systems/${systemId}/zones`, { method: 'POST', body: zone }),
  updateZone: (zoneId, updates) =>
    request(`/zones/${zoneId}`, { method: 'PATCH', body: updates }),
  deleteZone: (zoneId) => request(`/zones/${zoneId}`, { method: 'DELETE' }),

  lightPrograms: (systemId) => request(`/systems/${systemId}/lights`),
  createLightProgram: (systemId, program) =>
    request(`/systems/${systemId}/lights`, { method: 'POST', body: program }),
  updateLightProgram: (programId, updates) =>
    request(`/lights/${programId}`, { method: 'PATCH', body: updates }),
  deleteLightProgram: (programId) =>
    request(`/lights/${programId}`, { method: 'DELETE' }),

  nodes: () => request('/nodes'),
  node: (nodeId) => request(`/nodes/${nodeId}`),
  claimNode: (code, name, systemId) =>
    request('/nodes/claim', { method: 'POST', body: { code, name, system_id: systemId } }),

  catalog: () => request('/catalog'),
  addModule: (nodeId, module) =>
    request(`/nodes/${nodeId}/modules`, { method: 'POST', body: module }),
  // `force` overrides the dark-period lockout on a grow light and nothing
  // else; the API refuses a locked run with 409 and a sentence to show.
  runModule: (moduleId, seconds, dutyPercent = 100, force = false) =>
    request(`/modules/${moduleId}/run`, {
      method: 'POST',
      body: { seconds, duty_percent: dutyPercent, force },
    }),
  stopModule: (moduleId) => request(`/modules/${moduleId}/stop`, { method: 'POST' }),
  // `limit` is how many of the newest rows inside the window come back. The
  // default is small enough to crop a busy probe, so charts ask for more.
  readings: (moduleId, hours = 24, limit = 500) =>
    request(`/modules/${moduleId}/readings?hours=${hours}&limit=${limit}`),

  events: (nodeId) => request(`/nodes/${nodeId}/events`),
  alerts: () => request('/alerts'),
};
